// src/admin/pages/PermissionsPage.jsx
//
// Permission catalog admin page. Route: /permissions.
//
// Rebuilt on the shared/ui kit to match connectors/pages/ConnectorsPage.jsx
// and accounts/pages/AccountsPage.jsx: PageHeader with the action on the
// right, a StatTile summary row, one DataTable, and create/edit behind a
// modal. Replaces pages/PermissionsPage.jsx + pages/CreatePermissionForm.jsx
// (a hand-rolled <table>, an always-open inline form, window.confirm, and
// styles/PermissionsPage.css) — all of which are deleted with this change.
//
// The whole catalog is loaded up front and DataTable sorts/searches/paginates
// it client-side, the same as AccountsPage. It's a bounded, slow-moving
// reference list (~60 rows), and api/permissionsApi.listPermissions walks the
// server's 100-row page cap to assemble it.

import { useCallback, useEffect, useMemo, useState } from "react";
import { KeyRound, Plus, Layers, ShieldCheck } from "lucide-react";
import {
  PageHeader,
  Button,
  EmptyState,
  DataTable,
  StatusBadge,
  StatTile,
  Alert,
  ConfirmModal,
  SkeletonTable,
} from "../../shared/ui";
import {
  listPermissions,
  createPermission,
  updatePermission,
  deactivatePermission,
} from "../api/permissionsApi";
import { useAuth } from "../../context/AuthContext";
import PermissionFormModal from "../components/PermissionFormModal";

export default function PermissionsPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("identity:permissions:manage");

  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);

  // null = closed. { permission: null } = create, { permission: rec } = edit.
  const [formTarget, setFormTarget] = useState(null);
  const [deactivateTarget, setDeactivateTarget] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      setPermissions(await listPermissions());
    } catch (err) {
      setLoadError(err?.message || "Failed to load permissions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleSubmit = useCallback(
    async (values) => {
      const editing = formTarget?.permission;
      const saved = editing
        ? await updatePermission(editing.id, values)
        : await createPermission(values);
      await refresh();
      return saved;
    },
    [formTarget, refresh]
  );

  const handleDeactivate = useCallback(async () => {
    setActionError(null);
    await deactivatePermission(deactivateTarget.id);
    setDeactivateTarget(null);
    await refresh();
  }, [deactivateTarget, refresh]);

  const activeCount = permissions.filter((p) => p.isActive).length;
  const moduleCount = new Set(permissions.map((p) => p.module)).size;

  const columns = useMemo(
    () => [
      {
        accessorKey: "name",
        header: "Permission",
        cell: ({ getValue }) => (
          // Rendered verbatim and in mono: this exact string is the identifier
          // roles reference and require_permission() looks up, so it must be
          // readable character-for-character rather than prettified.
          <span className="font-mono text-[13px] font-medium text-ink">{getValue()}</span>
        ),
      },
      {
        accessorKey: "module",
        header: "Module",
        width: 150,
        cell: ({ getValue }) => (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-brand-50 text-brand-700 border border-brand-100 text-[11px] font-semibold">
            {getValue()}
          </span>
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ getValue }) => (
          <span className="text-muted">{getValue() || "—"}</span>
        ),
      },
      {
        accessorKey: "isActive",
        header: "Status",
        width: 120,
        cell: ({ getValue }) => (
          <StatusBadge map="record" status={getValue() ? "active" : "inactive"} size="sm" />
        ),
      },
      ...(canManage
        ? [
            {
              id: "actions",
              header: "",
              align: "right",
              width: 170,
              sortAllowed: false,
              cell: ({ row }) => (
                <div className="flex items-center justify-end gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setFormTarget({ permission: row.original })}
                  >
                    Edit
                  </Button>
                  {row.original.isActive && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:text-red-700"
                      onClick={() => setDeactivateTarget(row.original)}
                    >
                      Deactivate
                    </Button>
                  )}
                </div>
              ),
            },
          ]
        : []),
    ],
    [canManage]
  );

  return (
    <div>
      <PageHeader
        title="Permissions"
        subtitle="Every action a role can grant. A route checks one of these by its exact name."
        actions={
          canManage && (
            <Button variant="primary" onClick={() => setFormTarget({ permission: null })}>
              <Plus size={15} /> New permission
            </Button>
          )
        }
      />

      {!loading && !loadError && permissions.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
          <StatTile icon={KeyRound} label="Total Permissions" value={permissions.length} />
          <StatTile icon={ShieldCheck} label="Active" value={activeCount} />
          <StatTile icon={Layers} label="Modules Covered" value={moduleCount} />
        </div>
      )}

      {actionError && (
        <Alert variant="danger" title="Something went wrong" className="mb-4">
          {actionError}
        </Alert>
      )}

      {loading ? (
        <SkeletonTable rows={5} columns={canManage ? 5 : 4} />
      ) : loadError ? (
        <EmptyState
          icon={KeyRound}
          title="Couldn't load permissions"
          description={loadError}
          action={
            <Button variant="secondary" onClick={refresh}>
              Try again
            </Button>
          }
        />
      ) : permissions.length === 0 ? (
        <EmptyState
          icon={KeyRound}
          title="No permissions yet"
          description="Create the first permission, then attach it to a role to grant it."
          action={
            canManage && (
              <Button variant="secondary" onClick={() => setFormTarget({ permission: null })}>
                <Plus size={15} /> New permission
              </Button>
            )
          }
        />
      ) : (
        <DataTable table={{ columns, rows: permissions }} canSearch />
      )}

      {/* Keyed so switching between create and editing a different row
          re-seeds the form's initial state instead of keeping stale input. */}
      <PermissionFormModal
        key={formTarget?.permission?.id ?? "new"}
        open={Boolean(formTarget)}
        permission={formTarget?.permission || null}
        onClose={() => setFormTarget(null)}
        onSubmit={handleSubmit}
      />

      <ConfirmModal
        open={Boolean(deactivateTarget)}
        entityLabel="permission"
        name={deactivateTarget?.name}
        onClose={() => setDeactivateTarget(null)}
        onConfirm={handleDeactivate}
      >
        Roles referencing it keep the row, but it stops granting access to anyone who
        holds it. Existing sessions keep their current permissions until the user signs
        in again.
      </ConfirmModal>
    </div>
  );
}
